import {
  type CSSProperties,
  useState,
} from 'react'
import { isValidAccentColor } from '../../services/workspaceBrandingService'
import './workspaceBrandChip.css'

export type WorkspaceBrandChipSize = 24 | 28

export type WorkspaceBrandChipFallback = 'letter' | 'none'

export type WorkspaceBrandChipProps = {
  name: string
  logoUrl?: string | null
  accentColor?: string | null
  size?: WorkspaceBrandChipSize
  className?: string
  style?: CSSProperties
  fallback?: WorkspaceBrandChipFallback
}

const join = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ')

/**
 * WorkspaceBrandChip — shows the workspace logo (squircle mask, object-fit contain)
 * or the first letter of the workspace name.
 *
 * When fallback === 'none':
 * - If a logo is set and loads, renders a plain <img> with no squircle border or background tint.
 * - If no logo is set (or load fails), renders null.
 *
 * Border and background come from `--workspace-accent`, set inline on the chip only
 * (fallback `var(--accent)` when accentColor is invalid or empty). Never touches
 * `--accent` or the theme tokens. No colour literals other than the user's own accent value.
 * A logo that fails to load falls back to the letter when fallback === 'letter'.
 */
export function WorkspaceBrandChip({
  name,
  logoUrl,
  accentColor,
  size = 24,
  className,
  style,
  fallback = 'letter',
}: WorkspaceBrandChipProps) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)

  const showLogo = Boolean(logoUrl) && failedUrl !== logoUrl

  if (fallback === 'none') {
    if (!showLogo || !logoUrl) return null
    return (
      <img
        src={logoUrl}
        alt={name}
        className={join('object-contain shrink-0', className)}
        style={{ width: size, height: size, ...style }}
        onError={() => setFailedUrl(logoUrl)}
        aria-hidden="true"
      />
    )
  }

  const hasValidAccent = isValidAccentColor(accentColor)
  const effectiveAccent = hasValidAccent && typeof accentColor === 'string'
    ? accentColor.trim()
    : 'var(--accent)'

  const chipStyle: CSSProperties = {
    ['--workspace-accent' as string]: effectiveAccent,
    borderColor: 'var(--workspace-accent)',
    backgroundColor: 'color-mix(in srgb, var(--workspace-accent) 16%, transparent)',
    color: 'var(--workspace-accent)',
    ...style,
  }

  const trimmedName = name.trim()
  const firstLetter = trimmedName.length > 0 ? trimmedName.charAt(0).toUpperCase() : '?'

  return (
    <span
      className={join('workspaceBrandChip', `workspaceBrandChip--${size}`, className)}
      style={chipStyle}
      aria-hidden="true"
    >
      {showLogo && logoUrl ? (
        <img
          src={logoUrl}
          alt={name}
          className="workspaceBrandChip__logo"
          style={{ objectFit: 'contain' }}
          onError={() => setFailedUrl(logoUrl)}
        />
      ) : (
        <span className="workspaceBrandChip__letter">{firstLetter}</span>
      )}
    </span>
  )
}
